import { useMemo, useRef, useState } from "react";
import { AlertCircle, Check, ChevronDown, Download, Loader2, RefreshCw, WandSparkles } from "lucide-react";
import { AD_ASPECT_RATIOS, AD_ASPECT_RATIO_OPTIONS, type AspectRatio } from "@shared/aspectRatios";
import type { IterationSizeController } from "@/lib/iteration-size-downloads";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

export type IterationSizeItem = {
  resultId: string;
  adId: string;
  name: string;
  currentRatio: AspectRatio;
};

export type IterationSizeDownloadControlsProps = {
  setId: string;
  items: IterationSizeItem[];
  excluded: Array<{ name: string; reason: string }>;
  tile?: boolean;
  controller: IterationSizeController;
};

const optionByRatio = new Map(AD_ASPECT_RATIO_OPTIONS.map(option => [option.value, option]));
type SizeStatus = NonNullable<ReturnType<IterationSizeController["get"]>>["status"];

function statusLabel(status: SizeStatus | undefined) {
  if (!status) return "Not requested";
  return status === "uncertain" ? "Check status" : status[0].toUpperCase() + status.slice(1);
}

export function IterationSizeDownloadControls({
  setId,
  items,
  excluded,
  tile = false,
  controller,
}: IterationSizeDownloadControlsProps) {
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<Set<AspectRatio>>(() =>
    new Set(tile ? [] : AD_ASPECT_RATIOS),
  );
  const [paidConfirmed, setPaidConfirmed] = useState(false);
  const [localBusy, setLocalBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const clickLock = useRef(false);
  const busy = controller.busy || localBusy;
  const validItems = useMemo(() => items.filter(item => !!item.adId), [items]);
  const isBatch = !tile;
  const checkboxScope = tile ? `tile-${validItems[0]?.resultId ?? setId}` : `set-${setId}`;
  const activeRatios = AD_ASPECT_RATIOS.filter(ratio => selected.has(ratio) && validItems.some(item => item.currentRatio !== ratio));

  const itemForRatio = (ratio: AspectRatio) => validItems.filter(item => item.currentRatio !== ratio);
  const statusFor = (item: IterationSizeItem, ratio: AspectRatio) => controller.get(item.adId, ratio);
  const eligibleNewItems = (ratio: AspectRatio) => itemForRatio(ratio).filter(item => {
    const status = statusFor(item, ratio)?.status;
    return status === undefined || status === "pending" && !controller.busy;
  });
  const plannedCount = activeRatios.reduce((sum, ratio) => sum + eligibleNewItems(ratio).length, 0);
  const sameSizeExclusions = activeRatios.reduce((sum, ratio) => sum + validItems.filter(item => item.currentRatio === ratio).length, 0);
  const completedCount = activeRatios.reduce((sum, ratio) => sum + itemForRatio(ratio).filter(item => {
    const status = statusFor(item, ratio)?.status;
    return status === "done" || status === "running" || status === "pending";
  }).length, 0);
  const failedCount = AD_ASPECT_RATIOS.reduce((sum, ratio) =>
    sum + itemForRatio(ratio).filter(item => statusFor(item, ratio)?.status === "failed").length, 0);
  const hasDownloadable = activeRatios.some(ratio =>
    itemForRatio(ratio).some(item => statusFor(item, ratio)?.status === "done"));
  const uncertainCount = AD_ASPECT_RATIOS.reduce((sum, ratio) =>
    sum + itemForRatio(ratio).filter(item => statusFor(item, ratio)?.status === "uncertain").length, 0);

  const toggleRatio = (ratio: AspectRatio, checked: boolean) => {
    setSelected(previous => {
      const next = new Set(previous);
      if (checked) next.add(ratio);
      else next.delete(ratio);
      return next;
    });
    setPaidConfirmed(false);
    setError(null);
  };

  const perform = async (work: () => Promise<void>) => {
    if (clickLock.current || busy) return;
    clickLock.current = true;
    setLocalBusy(true);
    setError(null);
    try {
      await work();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "The size operation could not be completed. Try again.");
    } finally {
      clickLock.current = false;
      setLocalBusy(false);
    }
  };

  const runAndPackage = (retryItemsByRatio: Map<AspectRatio, IterationSizeItem[]> | null = null) => perform(async () => {
    if (!retryItemsByRatio) {
      await controller.run(setId, validItems, activeRatios);
      await controller.download(setId, validItems, activeRatios, isBatch);
      return;
    }
    await Promise.all([...retryItemsByRatio].map(([ratio, targets]) =>
      controller.run(setId, targets, [ratio], true),
    ));
    await controller.download(setId, validItems, [...retryItemsByRatio.keys()], isBatch);
  });

  const downloadAgain = () => perform(() => controller.download(setId, validItems, activeRatios, isBatch));
  const retryFailedRatio = (ratio: AspectRatio) => {
    const failedItems = itemForRatio(ratio).filter(item => statusFor(item, ratio)?.status === "failed");
    return runAndPackage(new Map([[ratio, failedItems]]));
  };
  const retryAllFailed = () => {
    const byRatio = new Map<AspectRatio, IterationSizeItem[]>();
    for (const ratio of AD_ASPECT_RATIOS) {
      const failedItems = itemForRatio(ratio).filter(item => statusFor(item, ratio)?.status === "failed");
      if (failedItems.length) byRatio.set(ratio, failedItems);
    }
    return runAndPackage(byRatio);
  };
  const checkUncertainStatus = () => perform(async () => {
    const byRatio = new Map<AspectRatio, IterationSizeItem[]>();
    for (const ratio of AD_ASPECT_RATIOS) {
      const uncertain = itemForRatio(ratio).filter(item => statusFor(item, ratio)?.status === "uncertain");
      if (uncertain.length) byRatio.set(ratio, uncertain);
    }
    await Promise.all([...byRatio].map(([ratio, targets]) =>
      controller.checkStatus(setId, targets, [ratio]),
    ));
  });

  const triggerLabel = tile ? "Download with rerender" : "Download all in other sizes";
  const noSelection = activeRatios.length === 0;
  const runBlocked = busy || noSelection || plannedCount === 0 || (isBatch && !paidConfirmed);
  const namedStatusItems = AD_ASPECT_RATIOS.flatMap(ratio =>
    itemForRatio(ratio).flatMap(item => {
      const state = statusFor(item, ratio);
      return state && (state.status === "failed" || state.status === "uncertain")
        ? [{ item, ratio, state }]
        : [];
    }),
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          size="sm"
          variant="outline"
          className={tile ? "h-7 w-full gap-1 px-2 text-[10px]" : "h-8 gap-1.5 px-2.5 text-xs"}
          aria-label={triggerLabel}
          data-testid={tile ? `button-iteration-sizes-${setId}` : `button-download-other-sizes-${setId}`}
        >
          {tile ? <WandSparkles className="h-3.5 w-3.5" /> : <Download className="h-3.5 w-3.5" />}
          {triggerLabel}
          <ChevronDown className="h-3 w-3 opacity-60" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" sideOffset={7} className="w-[min(22rem,calc(100vw-1.5rem))] p-0" aria-label={`${triggerLabel} options`}>
        <div className="border-b border-border/70 px-3.5 py-3">
          <p className="text-sm font-semibold tracking-tight">Download in other sizes</p>
          <p className="mt-0.5 text-[11px] leading-relaxed text-muted-foreground">
            {tile ? `For ${validItems[0]?.name ?? "this ad"}` : `${validItems.length} ads in this set`}
          </p>
          {tile && <p className="mt-1 text-[11px] leading-relaxed text-muted-foreground">
            Uses the same reframe engine as My Ads to resize this creative. Your original stays unchanged; downloaded sizes are not saved to My Ads.
          </p>}
        </div>

        <div className="max-h-64 space-y-1 overflow-y-auto p-2">
          {AD_ASPECT_RATIOS.map(ratio => {
            const option = optionByRatio.get(ratio)!;
            const isCurrentTileRatio = tile && validItems[0]?.currentRatio === ratio;
            const ratioItems = itemForRatio(ratio);
            const failedHere = ratioItems.filter(item => statusFor(item, ratio)?.status === "failed");
            const runningHere = ratioItems.filter(item => ["pending", "running"].includes(statusFor(item, ratio)?.status ?? ""));
            const doneHere = ratioItems.filter(item => statusFor(item, ratio)?.status === "done");
            const uncertainHere = ratioItems.filter(item => statusFor(item, ratio)?.status === "uncertain");
            const unavailableForAll = !tile && ratioItems.length === 0;
            const checked = selected.has(ratio) && !unavailableForAll;
            return (
              <div key={ratio} className={`rounded-md px-2.5 py-2 ${isCurrentTileRatio ? "bg-muted/70" : "hover:bg-muted/45"}`}>
                <div className="flex items-start gap-2.5">
                  <Checkbox
                    id={`iteration-ratio-${checkboxScope}-${ratio.replace(":", "-")}`}
                    checked={isCurrentTileRatio ? true : checked}
                    disabled={isCurrentTileRatio || unavailableForAll || busy}
                    onCheckedChange={value => toggleRatio(ratio, value === true)}
                    aria-label={`${ratio}${isCurrentTileRatio ? " current size" : ""}`}
                    data-testid={`checkbox-iteration-ratio-${ratio.replace(":", "-")}`}
                    className="mt-0.5"
                  />
                  <label
                    htmlFor={`iteration-ratio-${checkboxScope}-${ratio.replace(":", "-")}`}
                    className={`min-w-0 flex-1 ${isCurrentTileRatio ? "cursor-not-allowed" : "cursor-pointer"}`}
                  >
                    <span className="flex items-center justify-between gap-2">
                      <span className="text-xs font-semibold">{option.label}{isCurrentTileRatio && <span className="ml-1.5 text-[10px] font-normal text-muted-foreground">Current</span>}</span>
                      <span className="font-mono text-[10px] text-muted-foreground">{option.dimensions}</span>
                    </span>
                    <span className="mt-0.5 flex flex-wrap gap-x-2 text-[10px] text-muted-foreground">
                      {isCurrentTileRatio ? <span>Already at this size</span> : (
                        <>
                          {tile ? <span>{statusLabel(statusFor(validItems[0], ratio)?.status)}</span> : unavailableForAll ? <span>Same size for every ad</span> : <span>{ratioItems.length} eligible</span>}
                          {doneHere.length > 0 && <span>{doneHere.length} ready</span>}
                          {runningHere.length > 0 && <span>{runningHere.length} in progress</span>}
                          {failedHere.length > 0 && <span className="text-destructive">{failedHere.length} failed</span>}
                          {uncertainHere.length > 0 && <span className="text-amber-700">{uncertainHere.length} status unknown</span>}
                        </>
                      )}
                    </span>
                  </label>
                </div>
                {!isCurrentTileRatio && failedHere.length > 0 && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="ml-6 mt-1 h-6 px-1.5 text-[10px] text-destructive hover:text-destructive"
                    onClick={() => void retryFailedRatio(ratio)}
                    disabled={busy || (isBatch && !paidConfirmed)}
                    data-testid={`button-retry-size-${ratio.replace(":", "-")}`}
                  >
                    <RefreshCw className="mr-1 h-3 w-3" /> Retry failed ({failedHere.length})
                  </Button>
                )}
              </div>
            );
          })}
        </div>

        <div className="space-y-2 border-t border-border/70 p-3">
          <p className="text-[11px] leading-relaxed text-foreground" data-testid="iteration-render-estimate">
            {plannedCount > 0
              ? `${activeRatios.length} size${activeRatios.length === 1 ? "" : "s"} ${plannedCount === activeRatios.length * validItems.length ? "×" : "across"} ${validItems.length} ad${validItems.length === 1 ? "" : "s"} ${plannedCount === activeRatios.length * validItems.length ? "=" : "→"} ${plannedCount} new render${plannedCount === 1 ? "" : "s"} planned${completedCount ? ` · ${completedCount} already done or in progress` : ""}.`
              : hasDownloadable
                ? "No new renders needed for these sizes."
                : "Choose at least one size with an unrequested result."}
          </p>
          {sameSizeExclusions > 0 && <p className="text-[10px] text-muted-foreground">{sameSizeExclusions} same-size selections excluded from the request count.</p>}
          {failedCount > 0 && <p className="text-[10px] text-muted-foreground">Retry failed only requests {failedCount} new reframes. Successful and uncertain results are not rerendered.</p>}
          {isBatch && (
            <label className="flex cursor-pointer items-start gap-2 rounded-md border border-border/70 bg-muted/35 p-2 text-[10px] leading-relaxed">
              <Checkbox checked={paidConfirmed} onCheckedChange={value => setPaidConfirmed(value === true)} disabled={busy} aria-label="Confirm batch renders" data-testid="checkbox-paid-confirmation" className="mt-0.5" />
              <span>Generate the selected sizes.</span>
            </label>
          )}
          {uncertainCount > 0 && <p className="flex items-start gap-1.5 text-[10px] leading-relaxed text-amber-800" role="status">
            <AlertCircle className="mt-0.5 h-3 w-3 shrink-0" /> {uncertainCount} result{uncertainCount === 1 ? "" : "s"} have an uncertain status. Check status before retrying; they will not be rerendered automatically.
          </p>}
          {namedStatusItems.length > 0 && <div className="rounded-md border border-border/70 bg-muted/30 px-2.5 py-2 text-[10px]" data-testid="iteration-item-statuses">
            <p className="mb-1 font-semibold text-muted-foreground">Needs attention</p>
            <ul className="space-y-1">
              {namedStatusItems.map(({ item, ratio, state }) => (
                <li key={`${item.resultId}-${ratio}`} className="leading-relaxed">
                  <span className={`font-semibold ${state.status === "failed" ? "text-destructive" : "text-amber-800"}`}>{item.name} · {ratio} · {state.status}</span>
                  {state.message && <span className="text-muted-foreground"> — {state.message}</span>}
                </li>
              ))}
            </ul>
          </div>}
          {excluded.length > 0 && <div className="rounded-md bg-muted/45 px-2.5 py-2 text-[10px]" data-testid="iteration-exclusions">
            <p className="mb-1 font-semibold text-muted-foreground">Not included ({excluded.length})</p>
            <ul className="space-y-0.5">{excluded.map((item, index) => <li key={`${item.name}-${index}`} className="leading-relaxed"><span className="font-medium">{item.name}:</span> {item.reason}</li>)}</ul>
          </div>}
          {controller.packagingFailures.length > 0 && <div className="rounded-md border border-destructive/25 bg-destructive/5 px-2.5 py-2 text-[10px]" data-testid="iteration-packaging-failures">
            <p className="mb-1 font-semibold text-destructive">Could not package</p>
            <ul className="space-y-0.5">{controller.packagingFailures.map((item, index) => <li key={`${item.name}-${index}`}><span className="font-medium">{item.name}:</span> {item.message}</li>)}</ul>
          </div>}
          {error && <p role="alert" className="flex items-start gap-1.5 text-[11px] text-destructive"><AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />{error}</p>}
          <div className="flex flex-wrap items-center justify-between gap-2 pt-0.5">
            {uncertainCount > 0 && <Button type="button" variant="ghost" size="sm" className="h-7 px-1.5 text-[10px]" onClick={() => void checkUncertainStatus()} disabled={busy} data-testid="button-check-size-status">
              <RefreshCw className="mr-1 h-3 w-3" /> Check status ({uncertainCount})
            </Button>}
            {failedCount > 0 && <Button type="button" variant="ghost" size="sm" className="h-7 px-1.5 text-[10px]" onClick={() => void retryAllFailed()} disabled={busy || (isBatch && !paidConfirmed)} data-testid="button-retry-all-failed">
              <RefreshCw className="mr-1 h-3 w-3" /> Retry failed only ({failedCount})
            </Button>}
            <div className="ml-auto flex flex-wrap justify-end gap-1.5">
              {controller.packagingFailures.length > 0 && <Button type="button" variant="outline" size="sm" className="h-7 px-2 text-[10px]" onClick={() => void perform(() => controller.download(setId, validItems, activeRatios, isBatch, true))} disabled={busy} data-testid="button-retry-failed-downloads">
                <RefreshCw className="mr-1 h-3 w-3" /> Retry failed downloads
              </Button>}
              {hasDownloadable && <Button type="button" variant="outline" size="sm" className="h-7 px-2 text-[10px]" onClick={() => void downloadAgain()} disabled={busy} data-testid="button-download-sizes-again">
                <Download className="mr-1 h-3 w-3" /> Download again
              </Button>}
              <Button
                type="button"
                size="sm"
                className="h-7 px-2.5 text-[10px]"
                onClick={() => void runAndPackage()}
                disabled={runBlocked}
                data-testid="button-run-size-download"
              >
                {busy ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : <Check className="mr-1 h-3 w-3" />}
                {plannedCount ? `Generate & download${isBatch ? ` (${plannedCount})` : ""}` : "Generate"}
              </Button>
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default IterationSizeDownloadControls;
